import { JSONContent } from '@tiptap/react';
import type { Dispatch, SetStateAction } from 'react';
import type { Node } from 'reactflow';
import { getFileDisplaySize } from './fileUtils';
import { flowEditorState } from '../flowEditorState';
import { inheritedPersonaId, type NodeData } from '../nodes/flowEditorBlockNodeTypes';

export function assignPersonaId(jsonContent: JSONContent) {
  if (jsonContent.attrs) {
    jsonContent.attrs.personaId = flowEditorState.defaultPersonaId;
  } else {
    jsonContent.attrs = {
      personaId: flowEditorState.defaultPersonaId,
    };
  }

  return jsonContent;
}

type SetNodes = Dispatch<SetStateAction<Node<NodeData>[]>>;

// Append a media block (GIF, YouTube embed, ...) to a node's editor and write it
// back to the graph so the autosave and undo recorder both see it.
//
// The block must be written through a NEW nodes array (nodes.map -> a fresh node
// and data object). React Flow's controlled `nodes` prop and the recorder's
// post-commit [nodes, edges] effect both detect change by array/object identity;
// mutating node.data in place and returning the same array makes React bail out,
// so toObject() serialises stale content (the insert never saves) and the
// recorder never fires (the insert can't be undone).
//
// Returns false when the target node has no mounted editor (it isn't the
// selected node); the caller selects it to mount one, then retries on the next
// frame.
export function insertMediaIntoNode(
  setNodes: SetNodes,
  targetNodeId: string,
  block: JSONContent,
): boolean {
  const editor = flowEditorState.nodeIdToEditorMap.get(targetNodeId);
  if (!editor) {
    return false;
  }

  const root = editor.getJSON();

  // An options-only node serialises to `{ type: 'doc' }` with no `content` key at
  // all, so `editor.isEmpty && root.content` is false and the append branch's
  // `root.content?.push` no-ops, silently dropping the insert. Treat a
  // content-less doc as empty too, matching the message-insert path.
  if (editor.isEmpty || !root.content?.length) {
    root.content = [block];
  } else {
    const personaId = inheritedPersonaId(root);
    block.attrs ??= {};
    block.attrs.personaId = personaId;
    flowEditorState.defaultPersonaId = personaId;
    root.content?.push(block);
  }
  // emitUpdate:false: we write node.data ourselves via setNodes below and mark
  // dirty once. Letting setContent emit would fire the editor's onTipTapUpdate
  // and double the dirty/record work for a single insert.
  editor.commands.setContent(root, false);

  setNodes((nodes) =>
    nodes.map((node) =>
      node.id === targetNodeId ? { ...node, data: { ...node.data, tiptap: root } } : node,
    ),
  );
  // Mark dirty so the insert autosaves and the history recorder captures it. The
  // setContent above passes emitUpdate:false (see setContent call) so the editor
  // doesn't ALSO fire onTipTapUpdate, which would mark dirty a second time and
  // record a redundant graph write that only burst-coalescing saves us from.
  flowEditorState.markDirty();
  return true;
}

export function createFileJSON(file: File) {
  const { displayUnit, displaySize } = getFileDisplaySize(file);
  const fileNode: JSONContent = {
    type: 'fileComponent',
    attrs: {
      src: null,
      file: file,
      size: displaySize,
      sizeUnit: displayUnit,
      title: file.name,
      type: file.type,
    },
  };

  return fileNode;
}

export function createMediaJSON(file: File, mediaType: 'video' | 'img') {
  const imageNode: JSONContent = {
    type: 'resizableMedia',
    attrs: {
      src: null,
      file: file,
      height: '150px',
      width: '300px',
      'media-type': mediaType,
      title: file.name,
      alt: file.name,
    },
  };

  return imageNode;
}

export function createUploadDBlock(accept?: string) {
  return {
    type: 'upload-component',
    ...(accept ? { attrs: { accept } } : {}),
  };
}

export const messageJSON: JSONContent = {
  type: 'paragraph',
};
export const linkJSON: JSONContent = {
  type: 'paragraph',
  content: [
    {
      type: 'text',
      text: 'message',
      marks: [
        {
          type: 'link',
          attrs: {
            href: 'https://www.google.com/',
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
            class: null,
          },
        },
      ],
    },
  ],
};
export const ulJSON: JSONContent = {
  type: 'bulletList',
  content: [
    {
      type: 'listItem',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'item 1',
            },
          ],
        },
      ],
    },
    {
      type: 'listItem',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'item 2',
            },
          ],
        },
      ],
    },
    {
      type: 'listItem',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'item 3',
            },
          ],
        },
      ],
    },
  ],
};

export const olJSON: JSONContent = {
  type: 'orderedList',
  content: [
    {
      type: 'listItem',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'item 1',
            },
          ],
        },
      ],
    },
    {
      type: 'listItem',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'item 2',
            },
          ],
        },
      ],
    },
    {
      type: 'listItem',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'item 3',
            },
          ],
        },
      ],
    },
  ],
};
